/**
 * Clarity logomark — the "Lifted Lined Cloche".
 *
 * A restaurant serving cloche tipped open on its rim, an AI spark escaping from
 * the gap: Clarity lifts the lid on a restaurant's real numbers and serves the
 * insight. The double dome line is the premium detail; the slim curved dish keeps
 * it light rather than "platty".
 *
 * Geometry is copied verbatim from the brand masters (`brand/final/build.py`,
 * 256-unit grid) — edit there, not here. Two drawings:
 *   - `full`    — dome + inner line; for ≥ 40px (auth card, marketing).
 *   - `compact` — the same drawing tuned for ≤ 34px (rail, favicons): the inner
 *                 dome line is heavier and the gap wider so it doesn't fill in.
 *
 * Colours come from CSS (`.logo__mark`, `.logo__spark` in app.css), driven by the
 * teal tokens, so the mark follows the theme and never hard-codes hex.
 *
 * Accessibility: decorative by default (it always sits next to the "Clarity"
 * wordmark). Pass `label` when the mark stands alone so it is announced.
 */
type LogoVariant = 'full' | 'compact';

const FULL = {
  viewBox: '16 29 224 200',
  mark: [
    'M213.46 120.31A96 96 0 0 0 28 170L53.11 163.27A70 70 0 0 1 188.34 127.04Z',
    'M176.75 130.14A58 58 0 0 0 64.71 160.16L78.23 156.54A44 44 0 0 1 163.23 133.77Z',
    'M83.84 57.72A13 13 0 1 1 108.96 50.99L109.99 54.86L84.88 61.58Z',
    'M28 194Q128 210 228 194A7 7 0 0 1 230 208Q128 226 26 208A7 7 0 0 1 28 194Z',
  ],
  spark: 'M206 150Q209.5 168.5 228 172Q209.5 175.5 206 194Q202.5 175.5 184 172Q202.5 168.5 206 150Z',
};

const COMPACT = {
  viewBox: '9 27 238 200',
  mark: [
    'M213.46 118.31A96 96 0 0 0 28 168L55.05 160.75A68 68 0 0 1 186.41 125.55Z',
    'M170.96 129.69A52 52 0 0 0 70.5 156.61L87.89 151.95A34 34 0 0 1 153.57 134.35Z',
    'M81.91 56.24A15 15 0 1 1 110.89 48.47L111.92 52.34L82.95 60.1Z',
    'M26 188Q128 204 230 188A11 11 0 0 1 233 210Q128 230 23 210A11 11 0 0 1 26 188Z',
  ],
  spark: 'M208 132Q212.2 153.8 234 158Q212.2 162.2 208 184Q203.8 162.2 182 158Q203.8 153.8 208 132Z',
};

export function Logo({
  className,
  variant = 'full',
  label,
}: {
  className?: string;
  variant?: LogoVariant;
  /** Accessible name; omit when the mark sits beside the visible wordmark. */
  label?: string;
}) {
  const g = variant === 'compact' ? COMPACT : FULL;
  const a11y = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': true, focusable: false } as const);
  return (
    <svg
      className={className}
      viewBox={g.viewBox}
      xmlns="http://www.w3.org/2000/svg"
      data-variant={variant}
      {...a11y}
    >
      {g.mark.map((d) => (
        <path key={d} className="logo__mark" d={d} />
      ))}
      <path className="logo__spark" d={g.spark} />
    </svg>
  );
}
